import { ChangeEvent, FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { PollInput, PollOptionInput, PollType, RatingDisplay } from '@/types/NewsFeed'
import { Add, Close, Delete } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { SelectOptionsDeprecated } from '@/types/Select'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import DateTimePickerComponent from '@/components/_custom/Picker/dateTimePicker/dateTimePicker'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import moment from 'moment-timezone'
import styles from './pollCreator.module.scss'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'

const POLL_OPTION_MAX_LENGTH: number = 512
const MAX_POLL_OPTIONS: number = 10

interface PollCreatorProps {
    poll: PollInput
    onChange: (poll: PollInput) => void
    hasVotes?: boolean
}

const DEFAULT_OPTIONS: PollOptionInput[] = [
    { text: '', order: 0 },
    { text: '', order: 1 }
]

const PollCreator: FunctionComponent<PollCreatorProps> = ({ poll, onChange, hasVotes }: PollCreatorProps) => {
    const { t } = useTranslation()

    const typeOptions: SelectOptionsDeprecated = [
        {
            options: [
                { value: PollType.text_free, name: t('textFree') },
                { value: PollType.single_choice, name: t('singleChoice') },
                { value: PollType.multiple_choice, name: t('multipleChoice') },
                { value: PollType.rating, name: t('rating') }
            ]
        }
    ]

    const ratingOptions: SelectOptionsDeprecated = [
        {
            options: [
                { value: RatingDisplay.stars, name: t('ratingDisplay.stars') },
                { value: RatingDisplay.numbers, name: t('ratingDisplay.numbers') },
                { value: RatingDisplay.smileys, name: t('ratingDisplay.smileys') }
            ]
        }
    ]

    function handleTypeChange(value: string): void {
        if (!poll || hasVotes) return
        const type: PollType = value as PollType
        const needsOptions: boolean = type === PollType.single_choice || type === PollType.multiple_choice
        onChange({
            ...poll,
            type,
            options: needsOptions ? (poll.options?.length ? poll.options : [...DEFAULT_OPTIONS]) : undefined,
            ratingDisplay: type === PollType.rating ? (poll.ratingDisplay ?? RatingDisplay.stars) : undefined
        })
    }

    function handleOptionChange(index: number, text: string): void {
        if (!poll?.options || hasVotes) return
        const options: PollOptionInput[] = [...poll.options]
        options[index] = { ...options[index], text }
        onChange({ ...poll, options })
    }

    function addOption(): void {
        if (!poll?.options || hasVotes || poll.options.length >= MAX_POLL_OPTIONS) return
        onChange({ ...poll, options: [...poll.options, { text: '', order: poll.options.length }] })
    }

    function removeOption(index: number): void {
        if (!poll?.options || poll.options.length <= 2 || hasVotes) return
        const options: PollOptionInput[] = poll.options
            .filter((_: PollOptionInput, i: number): boolean => i !== index)
            .map((opt: PollOptionInput, i: number): PollOptionInput => ({ ...opt, order: i }))
        onChange({ ...poll, options })
    }

    function handleDeadlineChange(value: string): void {
        if (!poll) return

        onChange({ ...poll, deadline: value === '' ? undefined : value })
    }

    function handleRatingDisplayChange(value: string): void {
        if (!poll) return
        onChange({ ...poll, ratingDisplay: value as RatingDisplay })
    }

    function handleAnonymousChange(checked: boolean): void {
        if (hasVotes) return
        onChange({ ...poll, anonymous: checked })
    }

    const deadlineDisplayValue: string = poll?.deadline
        ? moment(poll.deadline, 'DD-MM-YYYY HH:mm').format('DD-MM-YYYY HH:mm')
        : ''

    return (
        <div className={styles.Content}>
            {hasVotes && <p className={styles.Info}>{t('pollTermsLocked')}</p>}

            <SelectUnique
                label={t('pollType')}
                selectOptions={typeOptions}
                selectValue={poll.type}
                setSelectValue={handleTypeChange}
                readOnly={hasVotes}
            />

            {(poll.type === PollType.single_choice || poll.type === PollType.multiple_choice) && (
                <div className={styles.Options}>
                    {poll.options?.map((opt: PollOptionInput, i: number) => (
                        <div key={i} className={styles.OptionRow}>
                            <input
                                className={styles.OptionInput}
                                value={opt.text}
                                placeholder={`${t('pollOption')} ${i + 1}`}
                                maxLength={POLL_OPTION_MAX_LENGTH}
                                disabled={hasVotes}
                                onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                                    handleOptionChange(i, e.target.value)
                                }
                            />
                            <IconButton
                                size='small'
                                onClick={(): void => removeOption(i)}
                                disabled={(poll.options?.length ?? 0) <= 2 || hasVotes}
                            >
                                <Close fontSize='small' />
                            </IconButton>
                        </div>
                    ))}
                    {!hasVotes && (poll.options?.length ?? 0) < MAX_POLL_OPTIONS && (
                        <ButtonTextIcon
                            type='custom'
                            customText={t('addPollOption')}
                            customIcon={<Add />}
                            onClick={addOption}
                        />
                    )}
                </div>
            )}

            {poll.type === PollType.rating && (
                <SelectUnique
                    label={t('ratingDisplay')}
                    selectOptions={ratingOptions}
                    selectValue={poll.ratingDisplay ?? RatingDisplay.stars}
                    setSelectValue={handleRatingDisplayChange}
                    readOnly={hasVotes}
                />
            )}

            <Checkbox
                onChange={handleAnonymousChange}
                disabled={hasVotes}
                checked={poll.anonymous ?? false}
                text={t('pollAnonymous')}
            />

            <div className={styles.DateTimeDeadline}>
                <DateTimePickerComponent
                    label={t('pollDeadline')}
                    value={deadlineDisplayValue}
                    setValue={handleDeadlineChange}
                    disablePast={false}
                    allowEmpty={true}
                    className={styles.DateTimePicker}
                />
                {deadlineDisplayValue !== '' && (
                    <Delete className={styles.Icon} onClick={() => handleDeadlineChange('')} />
                )}
            </div>
        </div>
    )
}

export default PollCreator
